/**
 * Display formatters for billing surfaces. Tiny module on purpose — nothing
 * here imports `plans.ts` or `usage.ts`, so it sits below them in the import
 * graph and the cost-anchored plan helpers can use it without circular deps.
 */

import { numberFormat } from '@/lib/i18n/format'
import { clamp } from '@/lib/shared/math/clamp'

const COMPACT: Intl.NumberFormatOptions = {
  notation: 'compact',
  maximumFractionDigits: 1,
}

/**
 * Compact count formatter (1.5K / 2.3M in English, in the reader's
 * `locale`). Sign-preserving — over-limit values (e.g. `-128` remaining
 * credits) round-trip with their `-` so the nav menu can surface a deficit
 * honestly instead of silently clamping to zero; negative thousands also
 * collapse to `-1.5K` style rather than `-1500`.
 */
export function formatTokenCount(num: number, locale: string): string {
  if (!Number.isFinite(num)) {
    return '0'
  }
  return Math.abs(num) >= 1000
    ? numberFormat(locale, COMPACT).format(num)
    : numberFormat(locale, { maximumFractionDigits: 0 }).format(num)
}

/**
 * Clamp a credit count to >= 0 for user-facing display.
 *
 * `remainingCredits` is plan headroom plus bonus minus active holds
 * (`deriveSpendableCredits`), so it no longer carries an overdraft deficit; an
 * in-flight hold can still push it a few credits below zero transiently, and
 * legacy cached rows may be negative. End users should NEVER be confronted
 * with a negative balance, so every user-facing surface (sidebar credits row,
 * `/plan` hero, tooltips) routes the value through this helper before
 * rendering.
 *
 * The credit gate (`assertCanSpendCredits`) rejects new submits via the
 * underlying `<= 0` check, so clamping at the display boundary changes nothing
 * functionally.
 *
 * Non-finite inputs (NaN, ±Infinity) collapse to 0 defensively.
 */
export function clampDisplayCredits(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  return Math.max(0, value)
}

/**
 * Clamp a USD amount to >= 0 for user-facing display. Mirrors
 * {@link clampDisplayCredits} so over-budget tooltips never expose a negative
 * dollar deficit (e.g. `-$1.28`) to end users.
 */
export function clampDisplayUsd(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  return Math.max(0, value)
}

/**
 * Compute the prompt-cache hit rate from a token tuple. Defined as
 *
 *   `cachedInputTokens / inputTokens`
 *
 * which is the share of total input tokens that were served from cache
 * rather than as fresh input or a 1.25× cache write. Returns `null` when
 * `inputTokens` is 0 — used by dashboard cells to render a friendly
 * em-dash instead of `0%`.
 *
 * IMPORTANT: per AI SDK (LanguageModelUsage.inputTokens docs),
 * `inputTokens` is the TOTAL prompt size and ALREADY INCLUDES the
 * cached + cacheWrite portions. The hit rate is therefore
 * `cachedInputTokens / inputTokens` — NOT `cachedInputTokens /
 * (inputTokens + cachedInputTokens + cacheCreationInputTokens)`,
 * which would double-count and under-report the hit rate by ~2x.
 * See [docs/domains/billing/billing-credit-economics.md](../../docs/domains/billing/billing-credit-economics.md)
 * "Critical token-accounting convention".
 *
 * This is the metric the orchestrator + email-agent caching audit
 * surfaces (admin usage tooling): a healthy production deployment lands
 * well above zero on `brew.email-agent` and `brew.orchestrator`. When
 * caching regressions ship — e.g. someone sneaks per-call data into the
 * static system prefix — the rate cliffs and the panel makes the loss
 * obvious without anyone needing to inspect raw token counts.
 */
export function computeCacheHitRate(args: {
  inputTokens: number
  cachedInputTokens: number
  /** Currently unused — kept on the signature so future variants of
   *  the metric (e.g. "reads-only" vs "reads + writes") can plug in
   *  without changing the public API. */
  cacheCreationInputTokens: number
}): number | null {
  void args.cacheCreationInputTokens
  const inputTokens = Math.max(0, args.inputTokens)
  if (inputTokens <= 0) {
    return null
  }
  const cached = Math.max(0, args.cachedInputTokens)
  // Clamp to [0, 1] in case Anthropic accounting drift ever pushes
  // cached above inputTokens (observed up to ~3% drift in production).
  return Math.min(1, cached / inputTokens)
}

/**
 * Render a cache-hit rate (from {@link computeCacheHitRate}) as a
 * percentage label. `null` becomes an em-dash so empty rows render
 * cleanly. The number is clamped to [0, 100] and rounded to 1 decimal
 * for tabular alignment.
 */
export function formatCacheHitRate(rate: number | null): string {
  if (rate === null) {
    return '—'
  }
  if (!Number.isFinite(rate)) {
    return '—'
  }
  const pct = clamp(rate * 100, 0, 100)
  return `${pct.toFixed(1)}%`
}
